import { useInvoice } from 'pages/invoices/hooks/useInvoice';
import { useSharedPayment } from 'pages/invoices/payers/useSharedPayment';
import { useCallback, useMemo } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useUpdateInvoiceRowsMutation } from 'store/api/invoices';
import { ClassifierData } from 'types/classifiers';
import {
    EditIntentTypeCode,
    InvoiceData,
    InvoiceDetailsTotals,
    InvoiceRowBulkUpdateDto,
    InvoiceRowCorrectionForm,
    InvoiceRowCorrectionFormRow,
    InvoiceRowForm,
} from 'types/invoices';
import InvoiceRowActions from './invoice-rows/InvoiceRowActions';
import { InvoiceRowAddButton } from './invoice-rows/InvoiceRowAddButton';
import InvoiceRowTotalsTableItem from './InvoiceRowTotalsTableItem';
import InvoiceTotalsTableHeader from './InvoiceTotalsTableHeader';
import { TotalsTableCard } from './TotalsTableCard';

interface Props {
    invoiceTotals?: InvoiceDetailsTotals;
    invoice: InvoiceData;
    classifiers: Record<string, ClassifierData>;
    isInViewMode: boolean;
    index: number;
    matterId: number;
    handleAddRow: (data: InvoiceRowForm) => void;
    paymentCurrency: string;
}

const InvoiceRowTotals = ({
    invoiceTotals,
    invoice,
    classifiers,
    isInViewMode,
    matterId,
    handleAddRow,
    paymentCurrency,
}: Props) => {
    const { isCredit, isSent, isCorrection: isCorrectionInvoice } = useInvoice(invoice);
    const isEditableBlankCreditNote = isCredit && !invoice.debitInvoice && !isSent;
    const isInViewModeForRows = isInViewMode && !isEditableBlankCreditNote;
    // A shared-payment copy cannot take a fee of its own: its amounts are the group's.
    const { isSharedPaymentCopy } = useSharedPayment(invoice);
    const [updateInvoiceRows] = useUpdateInvoiceRowsMutation();

    const otherFeeRows = useMemo(
        () => (invoiceTotals?.invoiceRowTotals ?? []).filter((row) => row.invoiceRowTypeCode !== 'ADMIN_FEE'),
        [invoiceTotals?.invoiceRowTotals]
    );

    const formValues: InvoiceRowCorrectionForm = useMemo(
        () => ({
            rows: otherFeeRows.map((row) => ({
                id: row.invoiceRowId,
                selected: false,
                editIntentTypeCode: row.editIntentTypeCode,
            })),
        }),
        [otherFeeRows]
    );

    const { control, getValues, setValue } = useForm<InvoiceRowCorrectionForm>({
        values: formValues,
    });

    const { fields, remove } = useFieldArray({
        control,
        name: 'rows',
        keyName: 'formKey',
    });

    const handleUpdateInvoiceRows = useCallback(
        async (idList: number[]) => {
            const dtoList: InvoiceRowBulkUpdateDto[] = [];
            const rows: InvoiceRowCorrectionFormRow[] = getValues('rows') || [];
            rows.filter((row) => idList.includes(row.id)).forEach((row) =>
                dtoList.push({
                    id: row.id,
                    editIntentTypeCode: row.editIntentTypeCode,
                })
            );

            await updateInvoiceRows({ invoiceId: invoice.id, matterId, dtoList }).unwrap().catch(console.error);
        },
        [getValues, invoice.id, matterId, updateInvoiceRows]
    );

    const handleCorrect = useCallback(() => {
        const rows: InvoiceRowCorrectionFormRow[] = getValues('rows') || [];
        const idList: number[] = [];
        const updatedRows = rows.map((invoiceRow) => {
            if (invoiceRow.id && invoiceRow.selected) {
                idList.push(invoiceRow.id);
                return {
                    ...invoiceRow,
                    selected: false,
                    editIntentTypeCode: EditIntentTypeCode.REMOVE_VIA_CORRECTION,
                };
            }
            return invoiceRow;
        });
        setValue('rows', updatedRows, { shouldDirty: true });

        handleUpdateInvoiceRows(idList);
    }, [getValues, handleUpdateInvoiceRows, setValue]);

    const tableContent = useMemo(
        () =>
            fields.map((field, fieldIndex) => {
                const backendRow = otherFeeRows.find((row) => row.invoiceRowId === field.id);
                return (
                    <InvoiceRowTotalsTableItem
                        key={field.formKey}
                        index={fieldIndex}
                        matterId={matterId}
                        invoiceRowTotals={backendRow}
                        classifiers={classifiers}
                        isInViewMode={isInViewModeForRows}
                        invoice={invoice}
                        remove={remove}
                        control={control}
                        setValue={setValue}
                        handleUpdateInvoiceRows={(idList) => {
                            void handleUpdateInvoiceRows(idList);
                        }}
                    />
                );
            }),
        [
            fields,
            matterId,
            otherFeeRows,
            classifiers,
            isInViewModeForRows,
            invoice,
            remove,
            control,
            setValue,
            handleUpdateInvoiceRows,
        ]
    );

    return (
        <TotalsTableCard
            sticky
            title={<FormattedMessage id="invoices.totals-tab.other-fees" />}
            isEmpty={!fields.length}
            actions={
                <div className="flex flex-row items-center gap-2">
                    {!isInViewModeForRows && !isSharedPaymentCopy ? (
                        <InvoiceRowAddButton
                            classifiers={classifiers}
                            invoice={invoice}
                            handleSubmit={handleAddRow}
                            matterId={matterId}
                        >
                            <FormattedMessage id="invoices.details.add-row" />
                        </InvoiceRowAddButton>
                    ) : null}
                    {!isInViewMode && isCorrectionInvoice && fields.length ? (
                        <InvoiceRowActions handleCorrect={handleCorrect} control={control} />
                    ) : null}
                </div>
            }
        >
            <InvoiceTotalsTableHeader paymentCurrency={paymentCurrency} />
            <div className="flex flex-col">{tableContent}</div>
        </TotalsTableCard>
    );
};

export default InvoiceRowTotals;
